// src/components/Slideshow.js
import { useState, useEffect, useRef, useCallback } from "react";
import { AlertCircle, Home, ArrowLeft, ArrowRight } from "lucide-react";
import "../styles/Slideshow.css";

/* ------------  Worker API config  ------------ */
const WORKER_URL = "https://r2-image-service.t9e2i3kal.workers.dev";

const FullscreenSlideshow = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shuffledMedia, setShuffledMedia] = useState([]);
  const [isEmptyState, setIsEmptyState] = useState(false);

  const videoRef = useRef(null);
  const imageTimerRef = useRef(null);
  const pollIntervalRef = useRef(null);

  /* ------------  Persist query‑string for Home link  ------------ */
  // Keeps whatever ?foo=bar&baz=qux params exist in the current URL.
  const queryString =
    typeof window !== "undefined" ? window.location.search : "";
  const homeUrl = queryString ? `/${queryString}` : "/";

  /* ------------  Shuffle array helper  ------------ */
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  /* ------------  Fetch objects via Worker  ------------ */
  const listWorkerMedia = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${WORKER_URL}/api/media`);
      const result = await res.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch media");
      }

      // Worker already returns [{ key, url, name, type }]
      setShuffledMedia(shuffleArray(result.data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ------------  Move to next item  ------------ */
  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex < shuffledMedia.length - 1) {
        return prevIndex + 1;
      }
      // All items shown, refresh the page (query‑string stays intact automatically)
      window.location.reload();
      return prevIndex;
    });
  }, [shuffledMedia.length]);

  /* ------------  Move to previous item  ------------ */
  const previousSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex > 0) {
        return prevIndex - 1;
      }
      return prevIndex;
    });
  }, []);

  /* ------------  Handle video end  ------------ */
  const handleVideoEnd = () => nextSlide();

  /* ------------  Handle image timer  ------------ */
  useEffect(() => {
    if (shuffledMedia.length === 0 || loading) return;

    const currentItem = shuffledMedia[currentIndex];
    if (!currentItem) return;

    if (currentItem.type === "image") {
      // Show images for 10 seconds
      imageTimerRef.current = setTimeout(nextSlide, 10_000);
    }

    return () => {
      clearTimeout(imageTimerRef.current);
      imageTimerRef.current = null;
    };
  }, [currentIndex, shuffledMedia, loading, nextSlide]);

  /* ------------  Initialize slideshow  ------------ */
  useEffect(() => {
    listWorkerMedia();
  }, [listWorkerMedia]);

  /* ------------  Auto‑start slideshow when media loads  ------------ */
  useEffect(() => {
    if (shuffledMedia.length > 0 && !loading) {
      setCurrentIndex(0);
    }
  }, [shuffledMedia, loading]);

  /* ------------  Silent polling for media when in empty state  ------------ */
  const silentMediaCheck = useCallback(async () => {
    try {
      const res = await fetch(`${WORKER_URL}/api/media`);
      const result = await res.json();

      if (result.success && result.data.length > 0) {
        setShuffledMedia(shuffleArray(result.data));
      }
    } catch (err) {
      console.error("Polling error:", err);
    }
  }, []);

  useEffect(() => {
    if (isEmptyState && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(silentMediaCheck, 30000);
    } else if (!isEmptyState && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isEmptyState, silentMediaCheck]);

  /* ------------  Update empty state flag  ------------ */
  useEffect(() => {
    setIsEmptyState(shuffledMedia.length === 0);
  }, [shuffledMedia]);

  /* ------------  Loading state  ------------ */
  if (loading) {
    return <div className="fullscreen"></div>;
  }

  /* ------------  Error state  ------------ */
  if (error) {
    return (
      <div className="fullscreen">
        <AlertCircle size={48} />
        <h2 className="heading">Failed to load slideshow</h2>
        <p className="message">{error}</p>
        <button onClick={listWorkerMedia} className="retry-button">
          Try again
        </button>
      </div>
    );
  }

  /* ------------  Empty state  ------------ */
  if (isEmptyState) {
    return (
      <div className="fullscreen">
        <img
          src="/images/datlogoA.png"
          alt="DAT Logo"
          className="empty-state-logo"
        />
        <button
          onClick={() => (window.location.href = homeUrl)}
          className="home-button"
          aria-label="Return to home"
        >
          <Home size={64} />
        </button>
      </div>
    );
  }

  const currentItem = shuffledMedia[currentIndex];

  /* ------------  Slideshow render  ------------ */
  return (
    <div className="container">
      {currentItem &&
        (currentItem.type === "video" ? (
          <video
            ref={videoRef}
            src={currentItem.url}
            className="media"
            autoPlay
            muted
            playsInline
            onEnded={handleVideoEnd}
            onError={() => {
              console.error("Video failed to load:", currentItem.name);
              nextSlide();
            }}
          />
        ) : (
          <img
            src={currentItem.url}
            alt={currentItem.name}
            className="media"
            onError={() => {
              console.error("Image failed to load:", currentItem.name);
              nextSlide();
            }}
          />
        ))}

      {/* Progress indicator */}
      <div className="progress">
        {currentIndex + 1} / {shuffledMedia.length}
      </div>

      {/* Skip buttons */}
      <div className="skip-buttons">
        <button
          onClick={previousSlide}
          className="skip-button"
          aria-label="Previous"
        >
          <ArrowLeft size={32} />
        </button>
        <button onClick={nextSlide} className="skip-button" aria-label="Next">
          <ArrowRight size={32} />
        </button>
      </div>

      {/* Home button (query‑string aware) */}
      <button
        onClick={() => (window.location.href = homeUrl)}
        className="home-button"
        aria-label="Return to home"
      >
        <Home size={64} />
      </button>

      {/* Logo overlay */}
      <img src="/images/datlogoA.png" alt="DAT Logo" className="logo-overlay" />
    </div>
  );
};

export default FullscreenSlideshow;
